import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Newspaper, CalendarDays, Quote, BookOpen, Heart, Sparkles,
  Users, GraduationCap, School, CalendarClock, MapPin, Phone,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { resolveImg } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useSite } from "@/context/SiteContext";
import { useSeo } from "@/hooks/useSeo";
import { IslamicDivider, IslamicPatternCorner } from "@/components/IslamicPattern";
import PpdbDialog from "@/components/PpdbDialog";
import ImageFallback from "@/components/ImageFallback";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const Section = ({ children, className = "" }) => (
  <motion.section
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-60px" }}
    variants={fade}
    className={className}
  >
    {children}
  </motion.section>
);

const SectionTitle = ({ eyebrow, title }) => (
  <div className="mb-8 text-center">
    <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700">{eyebrow}</p>
    <h2 className="mt-2 font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{title}</h2>
    <IslamicDivider className="mt-4" />
  </div>
);

const Dashboard = () => {
  const { settings } = useSite();
  const [news, setNews] = useState([]);
  const [activities, setActivities] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [vm, setVm] = useState({});

  useSeo(
    "Beranda",
    "Website resmi Madrasah Ibtidaiyah Futuhiyyah Doro, Pekalongan - pendidikan dasar Islam yang unggul dan berakhlak mulia."
  );

  useEffect(() => {
    api.get("/news?limit=3").then((r) => setNews(r.data)).catch(() => {});
    api.get("/activities?limit=3").then((r) => setActivities(r.data)).catch(() => {});
    api.get("/gallery").then((r) => setGallery(r.data.slice(0, 6))).catch(() => {});
    api.get("/vision-mission").then((r) => setVm(r.data)).catch(() => {});
  }, []);

  const stats = [
    { icon: GraduationCap, value: settings.stat_students, label: "Peserta Didik" },
    { icon: Users, value: settings.stat_teachers, label: "Guru & Staf" },
    { icon: School, value: settings.stat_classes, label: "Ruang Kelas" },
    { icon: CalendarClock, value: settings.stat_founded, label: "Tahun Berdiri" },
  ];

  const highlights = [
    { icon: BookOpen, title: "Tahfidz Al-Qur'an", desc: "Program hafalan Al-Qur'an juz 30 dengan bimbingan intensif." },
    { icon: Sparkles, title: "Kurikulum Modern", desc: "Memadukan Kurikulum Merdeka dengan muatan keislaman." },
    { icon: Heart, title: "Pembinaan Akhlak", desc: "Penguatan karakter dan akhlakul karimah setiap hari." },
  ];

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[78vh] overflow-hidden">
        <img src={resolveImg(settings.hero_image_url)} alt="Suasana MI Futuhiyyah Doro" className="absolute inset-0 h-full w-full object-cover" />
        <div className="hero-overlay absolute inset-0" />
        <IslamicPatternCorner className="pointer-events-none absolute -right-16 top-10 h-72 w-72 text-emerald-300/20" />
        <div className="relative mx-auto flex min-h-[78vh] max-w-6xl flex-col justify-center px-6 py-20">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-emerald-100 backdrop-blur-sm ring-1 ring-white/20">
              <MapPin className="h-3.5 w-3.5" /> Doro - Pekalongan, Jawa Tengah
            </span>
            <h1 className="mt-5 font-heading text-4xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
              Madrasah Ibtidaiyah <span className="text-emerald-300">Futuhiyyah</span>
            </h1>
            <p className="mt-4 max-w-xl text-base font-medium text-emerald-50/90 sm:text-lg">
              {settings.tagline}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/profil">
                <Button size="lg" className="gap-2 rounded-full bg-white text-emerald-800 hover:bg-emerald-50" data-testid="hero-profile-button">
                  Lihat Profil <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link to="/berita">
                <Button size="lg" variant="outline" className="gap-2 rounded-full border-white/40 bg-white/5 text-white backdrop-blur-sm hover:bg-white/15" data-testid="hero-news-button">
                  Berita & Kegiatan
                </Button>
              </Link>
              <PpdbDialog
                trigger={
                  <Button size="lg" className="gap-2 rounded-full bg-emerald-500 text-white hover:bg-emerald-600" data-testid="hero-ppdb-cta-button">
                    <GraduationCap className="h-5 w-5" /> Daftar Siswa Baru
                  </Button>
                }
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* HIGHLIGHTS */}
      <Section className="mx-auto max-w-6xl px-6 -mt-14 relative z-10">
        <div className="grid gap-5 sm:grid-cols-3">
          {highlights.map((h) => (
            <div key={h.title} className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
                <h.icon className="h-6 w-6" />
              </div>
              <h3 className="mt-4 font-heading text-lg font-semibold text-slate-800">{h.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{h.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* HEADMASTER WELCOME */}
      <Section className="mx-auto max-w-6xl px-6 py-16 sm:py-20">
        <div className="grid items-center gap-10 md:grid-cols-5">
          <div className="md:col-span-2">
            <div className="relative mx-auto max-w-xs">
              <div className="absolute -inset-3 rounded-[2.5rem] bg-emerald-100/70" />
              <img src={resolveImg(settings.headmaster_photo_url)} alt={settings.headmaster_name} className="relative rounded-t-[2.5rem] rounded-b-2xl object-cover aspect-[3/4] w-full shadow-lg" />
            </div>
          </div>
          <div className="md:col-span-3">
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700">Sambutan Kepala Madrasah</p>
            <Quote className="mt-3 h-9 w-9 text-emerald-200" />
            <p className="mt-2 text-lg leading-relaxed text-slate-700">{settings.headmaster_message}</p>
            <div className="mt-6">
              <p className="font-heading text-lg font-bold text-slate-900">{settings.headmaster_name}</p>
              <p className="text-sm text-emerald-700">Kepala Madrasah Ibtidaiyah Futuhiyyah</p>
            </div>
          </div>
        </div>
      </Section>

      {/* ABOUT */}
      <Section className="islamic-pattern-bg border-y border-slate-100">
        <div className="mx-auto max-w-4xl px-6 py-16 text-center">
          <SectionTitle eyebrow="Tentang Kami" title="Mengenal MI Futuhiyyah" />
          <p className="text-base leading-relaxed text-slate-600">{settings.about_short}</p>
          <Link to="/profil">
            <Button variant="outline" className="mt-6 gap-2 rounded-full border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="about-more-button">
              Selengkapnya <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </Section>

      {/* STATS */}
      <Section className="bg-emerald-900">
        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-6 px-6 py-14 sm:grid-cols-4">
          {stats.map((s) => (
            <div key={s.label} className="text-center text-white">
              <s.icon className="mx-auto h-8 w-8 text-emerald-300" />
              <p className="mt-3 font-heading text-3xl font-extrabold sm:text-4xl">{s.value ?? "-"}</p>
              <p className="mt-1 text-sm text-emerald-200">{s.label}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* VISION TEASER */}
      <Section className="mx-auto max-w-6xl px-6 py-16 sm:py-20">
        <SectionTitle eyebrow="Arah & Tujuan" title="Visi & Misi Kami" />
        <div className="grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl bg-gradient-to-br from-emerald-700 to-emerald-900 p-8 text-white shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-200">Visi</p>
            <p className="mt-3 text-lg font-medium leading-relaxed">{vm.vision}</p>
          </div>
          <div className="rounded-2xl border border-slate-200/80 bg-white p-8 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700">Misi</p>
            <ul className="mt-3 space-y-2.5">
              {(vm.missions || []).slice(0, 4).map((m, i) => (
                <li key={i} className="flex gap-3 text-sm text-slate-600">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-[11px] font-bold text-emerald-700">{i + 1}</span>
                  {m}
                </li>
              ))}
            </ul>
            <Link to="/visi-misi">
              <Button variant="ghost" className="mt-4 gap-2 px-0 text-emerald-700 hover:bg-transparent hover:text-emerald-900" data-testid="vision-more-button">
                Lihat Selengkapnya <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </Section>

      {/* LATEST NEWS */}
      <Section className="islamic-pattern-bg border-y border-slate-100">
        <div className="mx-auto max-w-6xl px-6 py-16 sm:py-20">
          <div className="mb-8 flex items-end justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700">Informasi Terkini</p>
              <h2 className="mt-2 font-heading text-2xl font-bold text-slate-900 sm:text-3xl">Berita Terbaru</h2>
            </div>
            <Link to="/berita" className="hidden sm:block">
              <Button variant="outline" className="gap-2 rounded-full border-emerald-200 text-emerald-700" data-testid="news-all-button">
                Semua Berita <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {news.map((n) => (
              <Link key={n.id} to={`/berita/${n.slug}`} data-testid={`dashboard-news-card-${n.id}`} className="group overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md">
                <div className="relative aspect-[16/10] overflow-hidden">
                  {n.image_url ? <img src={resolveImg(n.image_url)} alt={n.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /> : <ImageFallback />}
                  <span className="absolute left-3 top-3 rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold text-white">{n.category}</span>
                </div>
                <div className="p-5">
                  <p className="flex items-center gap-1.5 text-xs text-slate-400"><Newspaper className="h-3.5 w-3.5" /> {formatDate(n.date)}</p>
                  <h3 className="mt-2 font-heading text-base font-semibold text-slate-800 line-clamp-2 group-hover:text-emerald-700">{n.title}</h3>
                  <p className="mt-2 text-sm text-slate-500 line-clamp-2">{n.excerpt}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-emerald-700">Baca Selengkapnya <ArrowRight className="h-3.5 w-3.5" /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </Section>

      {/* LATEST ACTIVITIES */}
      <Section className="mx-auto max-w-6xl px-6 py-16 sm:py-20">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700">Agenda Madrasah</p>
            <h2 className="mt-2 font-heading text-2xl font-bold text-slate-900 sm:text-3xl">Kegiatan Terbaru</h2>
          </div>
          <Link to="/kegiatan" className="hidden sm:block">
            <Button variant="outline" className="gap-2 rounded-full border-emerald-200 text-emerald-700" data-testid="activities-all-button">
              Semua Kegiatan <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {activities.map((a) => (
            <Link key={a.id} to={`/kegiatan/${a.slug}`} data-testid={`dashboard-activity-card-${a.id}`} className="group overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md">
              <div className="aspect-[16/10] overflow-hidden">
                {a.image_url ? <img src={resolveImg(a.image_url)} alt={a.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /> : <ImageFallback />}
              </div>
              <div className="p-5">
                <p className="flex items-center gap-1.5 text-xs text-slate-400"><CalendarDays className="h-3.5 w-3.5" /> {formatDate(a.date)}</p>
                <h3 className="mt-2 font-heading text-base font-semibold text-slate-800 line-clamp-2 group-hover:text-emerald-700">{a.title}</h3>
                <p className="mt-2 text-sm text-slate-500 line-clamp-2">{a.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      {/* GALLERY */}
      <Section className="islamic-pattern-bg border-y border-slate-100">
        <div className="mx-auto max-w-6xl px-6 py-16 sm:py-20">
          <SectionTitle eyebrow="Dokumentasi" title="Galeri Kegiatan" />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
            {gallery.map((g) => (
              <div key={g.id} className="group relative aspect-square overflow-hidden rounded-xl">
                <img src={resolveImg(g.image_url)} alt={g.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 flex items-end bg-gradient-to-t from-black/60 to-transparent p-3 opacity-0 transition-opacity group-hover:opacity-100">
                  <p className="text-xs font-medium text-white">{g.title}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/galeri">
              <Button variant="outline" className="gap-2 rounded-full border-emerald-200 text-emerald-700" data-testid="gallery-all-button">
                Lihat Galeri Lengkap <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <Section className="mx-auto max-w-6xl px-6 py-16">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-600 via-emerald-700 to-emerald-900 px-8 py-14 text-center shadow-lg">
          <IslamicPatternCorner className="pointer-events-none absolute -left-10 -top-10 h-56 w-56 text-white/10" />
          <IslamicPatternCorner className="pointer-events-none absolute -bottom-16 -right-10 h-56 w-56 text-white/10" />
          <div className="relative">
            <h2 className="font-heading text-2xl font-bold text-white sm:text-3xl">Kenali Lebih Dekat MI Futuhiyyah Doro</h2>
            <p className="mx-auto mt-3 max-w-xl text-emerald-50/90">Bergabunglah bersama kami dalam mendidik generasi Rabbani yang unggul dan berakhlak mulia.</p>
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              <Link to="/kontak">
                <Button size="lg" className="gap-2 rounded-full bg-white text-emerald-800 hover:bg-emerald-50" data-testid="cta-contact-button">
                  <Phone className="h-4 w-4" /> Hubungi Kami
                </Button>
              </Link>
              <PpdbDialog
                trigger={
                  <Button size="lg" variant="outline" className="gap-2 rounded-full border-white/40 bg-white/5 text-white hover:bg-white/15" data-testid="cta-ppdb-button">
                    <GraduationCap className="h-4 w-4" /> Pendaftaran Siswa Baru
                  </Button>
                }
              />
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default Dashboard;
